package com.appsyde.shared.app.presentation.ticket

import com.appsyde.shared.app.presentation.inbox.ExtractOrchestrator
import com.appsyde.shared.core.presentation.BaseViewModel
import com.appsyde.shared.modules.ai.domain.model.AiExtractV2
import com.appsyde.shared.modules.ai.domain.model.AiRefineAction
import com.appsyde.shared.modules.ai.domain.model.DetectedFieldV2
import com.appsyde.shared.modules.ai.domain.usecase.GenerateAiExtractUseCase
import com.appsyde.shared.modules.ai.domain.usecase.RegenerateContextUseCase
import com.appsyde.shared.modules.ai.domain.usecase.RefineTextUseCaseV2
import com.appsyde.shared.modules.ticket.domain.model.Direction
import com.appsyde.shared.modules.ticket.domain.model.Ticket
import com.appsyde.shared.modules.ticket.domain.model.TicketStatus
import com.appsyde.shared.modules.ticket.domain.model.TicketRespondState
import com.appsyde.shared.modules.ticket.domain.repository.EmailRepository
import com.appsyde.shared.modules.ticket.domain.usecase.GetTicketHistoryUseCase
import com.appsyde.shared.modules.ticket.domain.usecase.GetTicketOriginalHistoryUseCase
import com.appsyde.shared.modules.ticket.domain.usecase.AddMemoryUseCase
import com.appsyde.shared.modules.ticket.domain.usecase.ConfirmKeyUseCase
import com.appsyde.shared.modules.ticket.domain.usecase.AcquireLockUseCase
import com.appsyde.shared.modules.ticket.domain.usecase.AssignTicketUseCase
import com.appsyde.shared.modules.ticket.domain.usecase.GetTicketRespondStateUseCase
import com.appsyde.shared.modules.ticket.domain.usecase.RefreshTicketThreadUseCase
import com.appsyde.shared.modules.ticket.domain.usecase.TakeoverTicketUseCase
import com.appsyde.shared.modules.ticket.domain.usecase.ListTicketsUseCase
import com.appsyde.shared.modules.ticket.domain.usecase.SendReplyUseCase
import com.appsyde.shared.modules.ticket.domain.usecase.UpdateTicketStatusUseCase
import com.appsyde.shared.modules.ticket.domain.model.AcquireLockResult
import com.appsyde.shared.modules.ticket.domain.model.AssignTicketResult
import com.appsyde.shared.modules.ticket.domain.model.OutboundAttachmentForSend
import com.appsyde.shared.network.exception.InsufficientScopesException
import kotlinx.coroutines.CoroutineDispatcher
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlinx.coroutines.yield

/** Fichier prêt à envoyer (rempli par l'UI après lecture). */
data class SelectedOutboundFile(
    val id: String,
    val filename: String,
    val sizeBytes: Long,
    val mimeType: String,
    val contentBase64: String
)

/** Cached respond + optional extract for preloaded tickets. */
private data class PreloadedTicket(
    val respond: TicketRespondState,
    val extract: AiExtractV2?
)

/**
 * TicketViewModel V2 (Respond tab only) - segment actif
 *
 * Key points:
 * - Navigation (next/back/select) uses a single transition pipeline:
 *   Exiting -> (load new ticket with Entering) -> Idle
 * - Avoid canceling the current navigation job mid-transition (kills animations).
 * - Yield after Entering to let Compose/DOM paint before moving to Idle.
 */
class TicketViewModel(
    private val listTicketsUseCase: ListTicketsUseCase,
    private val getTicketRespondStateUseCase: GetTicketRespondStateUseCase,
    private val refreshTicketThreadUseCase: RefreshTicketThreadUseCase,
    private val generateAiExtractUseCase: GenerateAiExtractUseCase,
    private val regenerateContextUseCase: RegenerateContextUseCase,
    private val refineTextUseCaseV2: RefineTextUseCaseV2,
    private val sendReplyUseCase: SendReplyUseCase,
    private val updateTicketStatusUseCase: UpdateTicketStatusUseCase,
    private val addMemoryUseCase: AddMemoryUseCase,
    private val confirmKeyUseCase: ConfirmKeyUseCase,
    private val getTicketHistoryUseCase: GetTicketHistoryUseCase,
    private val getTicketOriginalHistoryUseCase: GetTicketOriginalHistoryUseCase,
    private val assignTicketUseCase: AssignTicketUseCase,
    private val takeoverTicketUseCase: TakeoverTicketUseCase,
    private val acquireLockUseCase: AcquireLockUseCase,
    private val emailRepository: EmailRepository,
    private val extractOrchestrator: ExtractOrchestrator,
    val dispatcher: CoroutineDispatcher = Dispatchers.Default
) : BaseViewModel() {

    var onPermissionsRequired: (() -> Unit)? = null

    private val _sendState = MutableStateFlow<SendState>(SendState.Idle)
    val sendState: StateFlow<SendState> = _sendState

    private val _unreadCount = MutableStateFlow(0)
    val unreadCount: StateFlow<Int> = _unreadCount

    private val _processedToday = MutableStateFlow(0)
    val processedToday: StateFlow<Int> = _processedToday

    private val _openCount = MutableStateFlow(0)
    val openCount: StateFlow<Int> = _openCount

    private val _waitingCount = MutableStateFlow(0)
    val waitingCount: StateFlow<Int> = _waitingCount

    private val _clarificationsSuggestedCount = MutableStateFlow(0)
    val clarificationsSuggestedCount: StateFlow<Int> = _clarificationsSuggestedCount

    private val _weather = MutableStateFlow("normal")
    val weather: StateFlow<String> = _weather

    private val _weatherPercentDelta = MutableStateFlow<Int?>(null)
    val weatherPercentDelta: StateFlow<Int?> = _weatherPercentDelta

    private val _ticketState = MutableStateFlow<TicketState>(TicketState.Loading)
    val ticketState: StateFlow<TicketState> = _ticketState

    private val _outboundAttachments = MutableStateFlow<List<SelectedOutboundFile>>(emptyList())
    val outboundAttachments: StateFlow<List<SelectedOutboundFile>> = _outboundAttachments

    private val _attachmentErrors = MutableStateFlow<List<String>>(emptyList())
    val attachmentErrors: StateFlow<List<String>> = _attachmentErrors

    /** Une fois ouvert depuis l’onglet Tickets (Traiter), contient le ticketId pour que la vue bascule en RESPOND. */
    private val _navigateToRespondTicketId = MutableStateFlow<String?>(null)
    val navigateToRespondTicketId: StateFlow<String?> = _navigateToRespondTicketId.asStateFlow()

    /** Mis à true quand on est en fin de liste (Répondre) et que l'utilisateur fait Send ou Skip → la vue doit basculer sur Tickets. */
    private val _navigateToTicketsView = MutableStateFlow(false)
    val navigateToTicketsView: StateFlow<Boolean> = _navigateToTicketsView.asStateFlow()

    fun clearNavigateToTicketsView() {
        _navigateToTicketsView.value = false
    }

    /** True pendant assign + acquire + navigation (bouton « Traiter » affiche un spinner). */
    private val _isOpeningForResponse = MutableStateFlow(false)
    val isOpeningForResponse: StateFlow<Boolean> = _isOpeningForResponse.asStateFlow()

    // ---------------------------------------------------------------------
    // Queue state
    // ---------------------------------------------------------------------

    private var tickets: List<Ticket> = emptyList()
    private var index: Int = 0
    // Keep in sync with list screen fetch size to avoid "selected ticket not found" gaps.
    var batchSize: Int = 100

    /** Preload the next N tickets (respond, and for the immediate next also AI extract). */
    private val preloadAhead: Int = 2
    private val preloadCache = mutableMapOf<String, PreloadedTicket>()
    private var preloadJob: Job? = null

    private val pollIntervalMs: Long = 10_000L
    private var pollJob: Job? = null
    private var lastPolledUnread: Int = 0

    private val navMutex = Mutex()

    /** One active "work" job at a time. Do NOT cancel it for nav actions; transitions rely on it completing. */
    private var loadJob: Job? = null

    private val exitDurationMs: Long = 150
    private val enterDurationMs: Long = 150

    /** Small delay after Entering to help CSS transition be applied before we advance to Idle. */
    private val frameDelayMs: Long = 40

    /** Serializes transitions so we never overlap Exiting/Entering for two nav actions. */
    private val transitionMutex = Mutex()

    private fun logV2(msg: String) = println("🟣 [V2] $msg")

    // ---------------------------------------------------------------------
    // Public API
    // ---------------------------------------------------------------------

    fun startV2() {
        loadJob?.cancel()
        loadJob = viewModelScope.launch(dispatcher) {
            navMutex.withLock {
                _ticketState.value = TicketState.Loading
                runCatching {
                    ensureTicketsLoaded()
                    index = 0
                    loadCurrentTicket(forceAi = false, transition = TicketTransitionState.Idle)
                }.onFailure { e ->
                    logV2("❌ startV2 failed: ${e::class.simpleName} ${e.message}")
                    val isEmptyList = e is NoSuchElementException && e.message?.contains("List is empty") == true
                    _ticketState.value = if (isEmptyList) TicketState.Empty(canGoBack = false) else TicketState.Loading
                }
            }
            refreshInboxStats()
            startPolling()
        }
    }

    fun skip(forceAi: Boolean = false) = navigate(NavDirection.Forward, forceAi)
    fun goBack(forceAi: Boolean = false) = navigate(NavDirection.Backward, forceAi)

    /**
     * Point d’entrée depuis l’onglet Tickets (bouton « Traiter ce ticket »).
     * Lance dans viewModelScope : assign + acquire lock, puis charge le ticket et signale la vue pour basculer en RESPOND.
     */
    fun openTicketForResponse(ticketId: String) {
        viewModelScope.launch(dispatcher) {
            _isOpeningForResponse.value = true
            try {
                runCatching {
                    when (val r = assignTicketUseCase(ticketId)) {
                        is AssignTicketResult.Success -> logV2("✅ assign ok ticketId=$ticketId changed=${r.changed}")
                        is AssignTicketResult.AssignedToOther -> logV2("⚠️ ticket already assigned to ${r.assignedToDisplayName}")
                    }
                }.onFailure { e -> logV2("❌ assign failed: ${e.message}") }
                runCatching {
                    when (acquireLockUseCase(ticketId)) {
                        is AcquireLockResult.Success -> logV2("✅ lock acquired ticketId=$ticketId")
                        else -> logV2("⚠️ lock not acquired (other or expired)")
                    }
                }.onFailure { e -> logV2("❌ acquireLock failed: ${e.message}") }
                refreshListPreservingCurrentTicket()
                selectTicketById(ticketId)
                _navigateToRespondTicketId.value = ticketId
            } finally {
                _isOpeningForResponse.value = false
            }
        }
    }

    fun clearNavigateToRespond() {
        _navigateToRespondTicketId.value = null
    }

    /**
     * Reprendre un ticket déjà attribué à quelqu'un d'autre (takeover).
     * Puis charge le ticket et bascule en RESPOND.
     */
    fun takeoverTicket(ticketId: String) {
        viewModelScope.launch(dispatcher) {
            runCatching {
                takeoverTicketUseCase(ticketId)
                logV2("✅ takeover ok ticketId=$ticketId")
            }.onFailure { e -> logV2("❌ takeover failed: ${e.message}") }
            runCatching { acquireLockUseCase(ticketId) }.onFailure { e -> logV2("❌ acquireLock after takeover: ${e.message}") }
            refreshListPreservingCurrentTicket()
            selectTicketById(ticketId)
            _navigateToRespondTicketId.value = ticketId
        }
    }

    /** Select a specific ticket from Tickets tab (Traiter). Uses the same transition pipeline as next/back. */
    fun selectTicketById(ticketId: String) {
        // IMPORTANT: do NOT cancel loadJob here, or you'll cut transitions.
        loadJob = viewModelScope.launch(dispatcher) {
            navMutex.withLock {
                runCatching {
                    ensureTicketsLoaded()

                    var idx = tickets.indexOfFirst { it.ticketId == ticketId }
                    // Ticket absent de la liste (ex. vient d'être assigné, liste assignedToMe pas encore à jour) → on l'injecte et on charge
                    if (idx < 0) {
                        logV2("→ ticketId=$ticketId not in list, injecting from getRespond")
                        val respond = getTicketRespondStateUseCase(ticketId)
                        tickets = listOf(respond.ticket)
                        index = 0
                        preloadCache.clear()
                        loadCurrentTicket(forceAi = false, transition = TicketTransitionState.Idle)
                        return@withLock
                    }

                    val currentId = (_ticketState.value as? TicketState.Success)?.respond?.ticket?.ticketId
                    if (currentId == ticketId) return@withLock

                    // When opening from list, put the selected ticket first so we're at "position 0".
                    // Otherwise one "Suivant" on the last ticket would immediately send the user back to Tickets.
                    val selected = tickets[idx]
                    tickets = listOf(selected) + tickets.filterIndexed { i, _ -> i != idx }
                    index = 0
                    preloadCache.clear()
                    runTransition(TicketTransitionState.Exiting) {
                        loadCurrentTicket(forceAi = false, transition = TicketTransitionState.Entering, fromNavigation = true)
                    }
                }.onFailure { e ->
                    logV2("❌ selectTicketById failed: ${e::class.simpleName} ${e.message}")
                    _ticketState.update { cur ->
                        if (cur is TicketState.Success) cur.copy(transition = TicketTransitionState.Idle) else cur
                    }
                }
            }
        }
    }

    fun onReplyTextChanged(text: String) {
        _ticketState.update { cur ->
            if (cur is TicketState.Success) cur.copy(replyText = text, isModified = true) else cur
        }
    }

    fun onEditingChanged(isEditing: Boolean) {
        // kept for feature parity
    }

    fun regenerateAi() {
        loadJob?.cancel()
        loadJob = viewModelScope.launch(dispatcher) {
            navMutex.withLock {
                val cur = _ticketState.value as? TicketState.Success ?: return@withLock
                val pivot = cur.pivotInboundMessageId?.takeIf { it.isNotBlank() } ?: return@withLock

                _ticketState.update {
                    if (it is TicketState.Success) it.copy(isRegenerating = true, stableReplyText = it.replyText) else it
                }

                runCatching {
                    val ai = generateAiExtractUseCase(messageId = pivot, force = true)
                    val newExtract = ai.extract
                    val newReply = newExtract.response ?: ""
                    val ticketId = cur.respond.ticket.ticketId ?: return@runCatching
                    extractOrchestrator.putExtract(ticketId, newExtract)

                    // Ticket is source of truth: refetch so state has the persisted suggested_action (and other cognitive fields)
                    val updatedRespond = getTicketRespondStateUseCase(ticketId)
                    val updatedSegment = buildActiveSegment(updatedRespond)
                    val payload = buildLoadedTicketPayload(updatedRespond, updatedSegment, newExtract)

                    _ticketState.update {
                        if (it is TicketState.Success) it.copy(
                            respond = updatedRespond,
                            pivotInboundMessageId = updatedSegment.pivotInboundId,
                            activeInbound = updatedSegment.pivotInbound,
                            activeMessages = updatedSegment.messages,
                            missingKeys = payload.missingKeys,
                            toConfirmKeys = payload.toConfirmKeys,
                            sidebarDetected = payload.sidebarDetected,
                            extract = newExtract,
                            replyText = newReply,
                            suggestedReplyText = newReply,
                            stableReplyText = null,
                            isRegenerating = false,
                            isModified = false
                        ) else it
                    }
                }.onFailure { e ->
                    logV2("❌ regenerateAi failed: ${e::class.simpleName} ${e.message}")
                    _ticketState.update {
                        if (it is TicketState.Success) it.copy(isRegenerating = false, stableReplyText = null) else it
                    }
                }
            }
        }
    }

    fun refineReply(text: String, action: AiRefineAction) {
        loadJob?.cancel()
        loadJob = viewModelScope.launch(dispatcher) {
            navMutex.withLock {
                val cur = _ticketState.value as? TicketState.Success ?: return@withLock
                val input = text.trim()
                if (input.isBlank()) return@withLock

                _ticketState.update {
                    if (it is TicketState.Success) it.copy(isRegenerating = true, stableReplyText = it.replyText) else it
                }

                runCatching {
                    val refined = refineTextUseCaseV2(text = input, action = action).trim()
                    _ticketState.update {
                        if (it is TicketState.Success) it.copy(
                            replyText = refined,
                            stableReplyText = null,
                            isRegenerating = false,
                            isModified = true
                        ) else it
                    }
                }.onFailure { e ->
                    logV2("❌ refineReply failed: ${e::class.simpleName} ${e.message}")
                    _ticketState.update {
                        if (it is TicketState.Success) it.copy(isRegenerating = false, stableReplyText = null) else it
                    }
                }
            }
        }
    }

    fun sendReply(body: String) {
        loadJob?.cancel()
        loadJob = viewModelScope.launch(dispatcher) {
            navMutex.withLock {
                val cur = _ticketState.value as? TicketState.Success ?: return@withLock
                if (_sendState.value is SendState.Sending) return@withLock

                val trimmed = body.trim()
                if (trimmed.isBlank()) {
                    _sendState.value = SendState.Error("Le message est vide.")
                    return@withLock
                }

                _sendState.value = SendState.Sending

                runCatching {
                    val ticketId = cur.respond.ticket.ticketId
                    val attachmentsForSend = _outboundAttachments.value.map {
                        OutboundAttachmentForSend(
                            filename = it.filename,
                            mimeType = it.mimeType,
                            contentBase64 = it.contentBase64,
                            sizeBytes = it.sizeBytes,
                            sha256 = null
                        )
                    }
                    val result = sendReplyUseCase(
                        ticketId = ticketId,
                        body = trimmed,
                        subject = null,
                        to = null,
                        attachments = attachmentsForSend
                    )
                    logV2("✅ send ok ticketId=${result.ticketId} outbound=${result.outboundMessageId}")

                    extractOrchestrator.invalidateExtract(result.ticketId)
                    viewModelScope.launch(dispatcher) {
                        val respond = getTicketRespondStateUseCase(result.ticketId)
                        val segment = buildActiveSegment(respond)
                        segment.pivotInboundId?.takeIf { it.isNotBlank() }?.let { msgId ->
                            extractOrchestrator.ensureExtract(
                                ticketId = result.ticketId,
                                messageId = msgId,
                                force = true
                            )
                        }
                    }

                    _ticketState.update { st ->
                        if (st is TicketState.Success) st.copy(replyText = trimmed, isModified = false) else st
                    }

                    _sendState.value = SendState.Success
                    _outboundAttachments.value = emptyList()
                    _attachmentErrors.value = emptyList()
                    refreshInboxStats()
                    delay(250)
                    _sendState.value = SendState.Idle

                    // Use nav pipeline (do not cancel the job)
                    navigate(NavDirection.Forward, forceAi = false)
                }.onFailure { e ->
                    logV2("❌ sendReply failed: ${e::class.simpleName} ${e.message}")
                    _sendState.value = SendState.Error(e.message ?: "Erreur inconnue")
                }
            }
        }
    }

    fun addOutboundAttachment(file: SelectedOutboundFile) {
        _outboundAttachments.update { it + file }
    }

    fun removeOutboundAttachment(id: String) {
        _outboundAttachments.update { list -> list.filter { it.id != id } }
    }

    fun clearOutboundAttachments() {
        _outboundAttachments.value = emptyList()
        _attachmentErrors.value = emptyList()
    }

    fun setAttachmentErrors(errors: List<String>) {
        _attachmentErrors.value = errors
    }

    fun updateStatus(newStatus: String) {
        loadJob?.cancel()
        loadJob = viewModelScope.launch(dispatcher) {
            navMutex.withLock {
                val cur = _ticketState.value as? TicketState.Success ?: return@withLock
                val ticketId = cur.respond.ticket.ticketId
                val shouldPreserveDraft = cur.isModified
                val draftToPreserve = cur.replyText
                runCatching {
                    updateTicketStatusUseCase(ticketId = ticketId, status = newStatus)
                    // Ne pas recharger tout le ticket : on met à jour uniquement le statut en local
                    // pour éviter que le titre "Prochaine action" et l'action recommandée changent
                    // (ils ne dépendent pas du statut, mais un reload peut renvoyer des données
                    // légèrement différentes et provoquer un flicker).
                    _ticketState.update { st ->
                        if (st is TicketState.Success && st.respond.ticket.ticketId == ticketId) {
                            val updatedTicket = st.respond.ticket.copy(status = newStatus)
                            st.copy(
                                respond = st.respond.copy(ticket = updatedTicket),
                                replyText = if (shouldPreserveDraft) draftToPreserve else st.replyText,
                                isModified = if (shouldPreserveDraft) true else st.isModified
                            )
                        } else {
                            st
                        }
                    }
                }.onFailure { e ->
                    logV2("❌ updateStatus failed: ${e::class.simpleName} ${e.message}")
                }
            }
        }
    }

    /**
     * Ajoute ou met à jour une donnée validée (ex. information manquante complétée par l'agent).
     * Met à jour le state local et appelle le backend pour persister.
     */
    fun addMemory(key: String, value: String) {
        val keyNorm = key.trim().lowercase()
        if (keyNorm.isBlank() || value.trim().isBlank()) return
        val cur = _ticketState.value as? TicketState.Success ?: return
        val ticketId = cur.respond.ticket.ticketId ?: return

        _ticketState.update { state ->
            when (state) {
                is TicketState.Success -> {
                    val newDetected = state.sidebarDetected
                        .filter { it.key?.trim()?.lowercase() != keyNorm }
                        .plus(DetectedFieldV2(key = keyNorm, label = null, value = value.trim(), status = "detected", source = "agent_entered"))
                    val newMissing = state.missingKeys.filter { it.trim().lowercase() != keyNorm }
                    state.copy(
                        sidebarDetected = newDetected,
                        missingKeys = newMissing
                    )
                }
                else -> state
            }
        }

        viewModelScope.launch(dispatcher) {
            runCatching {
                addMemoryUseCase(ticketId = ticketId, key = keyNorm, value = value.trim())
            }.onSuccess {
                val pivot = (_ticketState.value as? TicketState.Success)?.pivotInboundMessageId?.takeIf { it.isNotBlank() }
                if (pivot != null) {
                    _ticketState.update { if (it is TicketState.Success) it.copy(isRegenerating = true, stableReplyText = it.replyText) else it }
                    runCatching {
                        val newExtract = regenerateContextUseCase(pivot)
                        val newReply = newExtract.response?.trim()?.takeIf { it.isNotBlank() } ?: ""
                        extractOrchestrator.putExtract(ticketId, newExtract)
                        // Ticket = source de vérité : recharger le ticket pour avoir recommended_action (titre + why) à jour
                        val updatedRespond = getTicketRespondStateUseCase(ticketId)
                        val updatedSegment = buildActiveSegment(updatedRespond)
                        val payload = buildLoadedTicketPayload(updatedRespond, updatedSegment, newExtract)
                        _ticketState.update { s ->
                            if (s is TicketState.Success) {
                                // Ne jamais écraser le draft si l'utilisateur l'a modifié (source de vérité = front)
                                val replyToKeepOrRefresh = if (s.isModified) (s.stableReplyText ?: s.replyText) else newReply
                                s.copy(
                                    respond = updatedRespond,
                                    pivotInboundMessageId = updatedSegment.pivotInboundId,
                                    activeInbound = updatedSegment.pivotInbound,
                                    activeMessages = updatedSegment.messages,
                                    missingKeys = payload.missingKeys,
                                    toConfirmKeys = payload.toConfirmKeys,
                                    sidebarDetected = payload.sidebarDetected,
                                    extract = newExtract,
                                    replyText = replyToKeepOrRefresh,
                                    suggestedReplyText = newReply,
                                    isRegenerating = false,
                                    stableReplyText = null
                                )
                            } else s
                        }
                    }.onFailure { e ->
                        logV2("❌ regenerateContext after addMemory: ${e::class.simpleName} ${e.message}")
                        _ticketState.update { if (it is TicketState.Success) it.copy(isRegenerating = false, stableReplyText = null) else it }
                    }
                }
            }.onFailure { e ->
                logV2("❌ addMemory failed: ${e::class.simpleName} ${e.message}")
                _ticketState.value = cur
            }
        }
    }

    /**
     * Valide un élément "à confirmer" : retire la clé de toConfirmKeys et persiste côté backend.
     */
    fun confirmKey(key: String) {
        val keyNorm = key.trim().lowercase()
        if (keyNorm.isBlank()) return
        val cur = _ticketState.value as? TicketState.Success ?: return
        val ticketId = cur.respond.ticket.ticketId ?: return

        _ticketState.update { state ->
            when (state) {
                is TicketState.Success -> {
                    val newToConfirm = state.toConfirmKeys.filter { it.trim().lowercase() != keyNorm }
                    val newDetected = state.sidebarDetected.map { f ->
                        if (f.key?.trim()?.lowercase() == keyNorm) f.copy(source = "agent_confirmed") else f
                    }
                    state.copy(toConfirmKeys = newToConfirm, sidebarDetected = newDetected)
                }
                else -> state
            }
        }

        viewModelScope.launch(dispatcher) {
            runCatching {
                confirmKeyUseCase(ticketId = ticketId, key = keyNorm)
            }.onSuccess {
                val pivot = (_ticketState.value as? TicketState.Success)?.pivotInboundMessageId?.takeIf { it.isNotBlank() }
                if (pivot != null) {
                    _ticketState.update { if (it is TicketState.Success) it.copy(isRegenerating = true, stableReplyText = it.replyText) else it }
                    runCatching {
                        val newExtract = regenerateContextUseCase(pivot)
                        val newReply = newExtract.response?.trim()?.takeIf { it.isNotBlank() } ?: ""
                        extractOrchestrator.putExtract(ticketId, newExtract)
                        // Ticket = source de vérité : recharger le ticket pour avoir recommended_action (titre + why) à jour
                        val updatedRespond = getTicketRespondStateUseCase(ticketId)
                        val updatedSegment = buildActiveSegment(updatedRespond)
                        val payload = buildLoadedTicketPayload(updatedRespond, updatedSegment, newExtract)
                        _ticketState.update { s ->
                            if (s is TicketState.Success) {
                                // Ne jamais écraser le draft si l'utilisateur l'a modifié (source de vérité = front)
                                val replyToKeepOrRefresh = if (s.isModified) (s.stableReplyText ?: s.replyText) else newReply
                                s.copy(
                                    respond = updatedRespond,
                                    pivotInboundMessageId = updatedSegment.pivotInboundId,
                                    activeInbound = updatedSegment.pivotInbound,
                                    activeMessages = updatedSegment.messages,
                                    missingKeys = payload.missingKeys,
                                    toConfirmKeys = payload.toConfirmKeys,
                                    sidebarDetected = payload.sidebarDetected,
                                    extract = newExtract,
                                    replyText = replyToKeepOrRefresh,
                                    suggestedReplyText = newReply,
                                    isRegenerating = false,
                                    stableReplyText = null
                                )
                            } else s
                        }
                    }.onFailure { e ->
                        logV2("❌ regenerateContext after confirmKey: ${e::class.simpleName} ${e.message}")
                        _ticketState.update { if (it is TicketState.Success) it.copy(isRegenerating = false, stableReplyText = null) else it }
                    }
                }
            }.onFailure { e ->
                logV2("❌ confirmKey failed: ${e::class.simpleName} ${e.message}")
                _ticketState.value = cur
            }
        }
    }

    // ---------------------------------------------------------------------
    // Navigation core
    // ---------------------------------------------------------------------

    private enum class NavDirection { Forward, Backward }

    private fun navigate(direction: NavDirection, forceAi: Boolean) {
        // IMPORTANT: do NOT cancel loadJob here.
        loadJob = viewModelScope.launch(dispatcher) {
            navMutex.withLock {
                runCatching {
                    ensureTicketsLoaded()
                    val nav = navigableTickets()
                    if (nav.isEmpty()) return@withLock

                    val currentId = (_ticketState.value as? TicketState.Success)?.respond?.ticket?.ticketId
                    var currentIdx = nav.indexOfFirst { it.ticketId == currentId }
                    if (currentIdx < 0) {
                        // Ticket actuel fermé (ex. ouvert depuis la vue Tickets) : Suivant → premier navigable, Précédent → rien
                        if (direction == NavDirection.Backward) {
                            _ticketState.update { cur ->
                                if (cur is TicketState.Success) cur.copy(canGoBack = false) else cur
                            }
                            return@withLock
                        }
                        currentIdx = 0
                    }

                    var targetIdx = when (direction) {
                        NavDirection.Forward -> currentIdx + 1
                        NavDirection.Backward -> currentIdx - 1
                    }
                    // En fin de liste : Send/Skip → rediriger vers la vue Tickets au lieu de boucler sur le premier.
                    if (direction == NavDirection.Forward && targetIdx >= nav.size) {
                        _ticketState.update { cur ->
                            if (cur is TicketState.Success) cur.copy(replyText = "") else cur
                        }
                        _navigateToTicketsView.value = true
                        viewModelScope.launch(dispatcher) {
                            navMutex.withLock {
                                loadCurrentTicket(forceAi = false, transition = TicketTransitionState.Idle)
                            }
                        }
                        return@withLock
                    }
                    if (direction == NavDirection.Backward && targetIdx < 0) {
                        _ticketState.update { cur ->
                            if (cur is TicketState.Success) cur.copy(canGoBack = false) else cur
                        }
                        return@withLock
                    }

                    val targetTicket = nav[targetIdx]
                    val targetIndexInFull = tickets.indexOfFirst { it.ticketId == targetTicket.ticketId }
                    if (targetIndexInFull < 0) return@withLock

                    val exitState = when (direction) {
                        NavDirection.Forward -> TicketTransitionState.Exiting
                        NavDirection.Backward -> TicketTransitionState.BackExiting
                    }
                    val enterState = when (direction) {
                        NavDirection.Forward -> TicketTransitionState.Entering
                        NavDirection.Backward -> TicketTransitionState.BackEntering
                    }

                    navigateToIndex(
                        targetIndex = targetIndexInFull,
                        forceAi = forceAi,
                        exitState = exitState,
                        enterState = enterState
                    )
                }.onFailure { e ->
                    logV2("❌ navigate failed: ${e::class.simpleName} ${e.message}")
                    _ticketState.update { cur ->
                        if (cur is TicketState.Success) cur.copy(transition = TicketTransitionState.Idle) else cur
                    }
                }
            }
        }
    }

    private suspend fun navigateToIndex(
        targetIndex: Int,
        forceAi: Boolean,
        exitState: TicketTransitionState = TicketTransitionState.Exiting,
        enterState: TicketTransitionState = TicketTransitionState.Entering
    ) {
        ensureTicketsLoaded()
        if (tickets.isEmpty()) return

        val clamped = targetIndex.coerceIn(0, tickets.lastIndex)
        val targetTicketId = tickets.getOrNull(clamped)?.ticketId
        val currentTicketId = (_ticketState.value as? TicketState.Success)?.respond?.ticket?.ticketId
        // Ne pas skip si la liste a changé (ex. après refresh) : même index mais ticket différent
        if (clamped == index && targetTicketId == currentTicketId) return

        runTransition(exitState) {
            index = clamped
            loadCurrentTicket(
                forceAi = forceAi,
                transition = enterState,
                fromNavigation = true
            )
        }
    }

    private suspend fun runTransition(exitState: TicketTransitionState, block: suspend () -> Unit) {
        transitionMutex.withLock {
            // 1) Exit phase (only if we currently have a Success card)
            updateTransition(exitState)
            delay(exitDurationMs)

            // 2) Load new ticket with Entering transition
            block()

            // 3) Ensure Entering can paint before we start the enter timer and then switch to Idle
            yield()
            delay(frameDelayMs)

            delay(enterDurationMs)
            updateTransition(TicketTransitionState.Idle)
        }
    }

    private fun updateTransition(newTransition: TicketTransitionState) {
        logV2("transition -> $newTransition (ticket=${(_ticketState.value as? TicketState.Success)?.respond?.ticket?.ticketId})")

        _ticketState.update { cur ->
            if (cur is TicketState.Success) cur.copy(transition = newTransition) else cur
        }
    }

    // ---------------------------------------------------------------------
    // Polling / inbox stats
    // ---------------------------------------------------------------------

    private fun startPolling() {
        pollJob?.cancel()
        lastPolledUnread = _unreadCount.value
        pollJob = viewModelScope.launch(dispatcher) {
            while (true) {
                delay(pollIntervalMs)
                runCatching {
                    val stats = emailRepository.getInboxStats()
                    _unreadCount.value = stats.unread
                    _processedToday.value = stats.processedToday
                    _openCount.value = stats.openCount
                    _waitingCount.value = stats.waitingCount
                    _clarificationsSuggestedCount.value = stats.clarificationsSuggestedCount
                    _weather.value = stats.weather
                    _weatherPercentDelta.value = stats.weatherPercentDelta
                    if (stats.unread > lastPolledUnread) {
                        lastPolledUnread = stats.unread
                        refreshListPreservingCurrentTicket()
                        if (_ticketState.value is TicketState.Empty && tickets.isNotEmpty()) {
                            navMutex.withLock {
                                val nav = navigableTickets()
                                index = nav.firstOrNull()?.let { first -> tickets.indexOfFirst { it.ticketId == first.ticketId } } ?: 0
                                loadCurrentTicket(forceAi = false, transition = TicketTransitionState.Idle)
                            }
                        }
                    } else {
                        lastPolledUnread = stats.unread
                    }
                }.onFailure { e ->
                    logV2("⚠️ poll failed: ${e.message}")
                    if (e is InsufficientScopesException || e.cause is InsufficientScopesException) {
                        onPermissionsRequired?.invoke()
                    } else {
                        syncSidebarCountsFromTicketList()
                    }
                }
            }
        }
    }

    private suspend fun refreshListPreservingCurrentTicket() {
        val currentTicketId = (_ticketState.value as? TicketState.Success)?.respond?.ticket?.ticketId
        val newList = listTicketsUseCase(limit = batchSize, assignedToMe = true)
        val newIndex = currentTicketId
            ?.let { id -> newList.indexOfFirst { it.ticketId == id } }
            ?.takeIf { it >= 0 } ?: 0

        tickets = newList
        index = newIndex
        preloadCache.clear()
        logV2("→ list refreshed (unread increased) size=${newList.size} index=$newIndex")
    }

    private fun refreshInboxStats() {
        viewModelScope.launch(dispatcher) {
            runCatching {
                val stats = emailRepository.getInboxStats()
                _unreadCount.value = stats.unread
                _processedToday.value = stats.processedToday
                _openCount.value = stats.openCount
                _waitingCount.value = stats.waitingCount
                _clarificationsSuggestedCount.value = stats.clarificationsSuggestedCount
                _weather.value = stats.weather
                _weatherPercentDelta.value = stats.weatherPercentDelta
            }.onFailure { e ->
                logV2("⚠️ refreshInboxStats failed: ${e.message}")
                if (e is InsufficientScopesException || e.cause is InsufficientScopesException) {
                    onPermissionsRequired?.invoke()
                } else {
                    syncSidebarCountsFromTicketList()
                }
            }
        }
    }

    // ---------------------------------------------------------------------
    // History (unchanged)
    // ---------------------------------------------------------------------

    fun toggleHistoryForCurrentTicket() {
        loadJob?.cancel()
        loadJob = viewModelScope.launch(dispatcher) {
            navMutex.withLock {
                val cur = _ticketState.value as? TicketState.Success ?: return@withLock
                when (cur.history) {
                    is HistoryState.Hidden -> {
                        _ticketState.update { st ->
                            if (st is TicketState.Success) st.copy(history = HistoryState.Loading) else st
                        }
                        loadHistoryForCurrentTicketInternal(cur)
                    }
                    else -> {
                        _ticketState.update { st ->
                            if (st is TicketState.Success) st.copy(
                                history = HistoryState.Hidden,
                                originalHistory = OriginalHistoryState.Hidden
                            ) else st
                        }
                    }
                }
            }
        }
    }

    private suspend fun loadHistoryForCurrentTicketInternal(cur: TicketState.Success) {
        val ticketId = cur.respond.ticket.ticketId
        runCatching { getTicketHistoryUseCase(ticketId, limit = 20) }
            .onSuccess { items ->
                _ticketState.update { st ->
                    if (st is TicketState.Success) st.copy(
                        history = HistoryState.Loaded(items.sortedBy { it.sentAtMs })
                    ) else st
                }
            }
            .onFailure { e ->
                _ticketState.update { st ->
                    if (st is TicketState.Success) st.copy(
                        history = HistoryState.Error(e.message ?: "Erreur chargement historique")
                    ) else st
                }
            }
    }

    fun loadHistoryForCurrentTicket() {
        loadJob?.cancel()
        loadJob = viewModelScope.launch(dispatcher) {
            navMutex.withLock {
                val cur = _ticketState.value as? TicketState.Success ?: return@withLock
                if (cur.history is HistoryState.Loaded) return@withLock
                _ticketState.update { st ->
                    if (st is TicketState.Success) st.copy(history = HistoryState.Loading) else st
                }
                loadHistoryForCurrentTicketInternal(cur)
            }
        }
    }

    fun loadOriginalHistoryForCurrentTicket() {
        loadJob?.cancel()
        loadJob = viewModelScope.launch(dispatcher) {
            navMutex.withLock {
                val cur = _ticketState.value as? TicketState.Success ?: return@withLock

                if (cur.history is HistoryState.Hidden) {
                    _ticketState.update { st ->
                        if (st is TicketState.Success) st.copy(history = HistoryState.Loading) else st
                    }
                    loadHistoryForCurrentTicketInternal(cur)
                }

                val cur2 = _ticketState.value as? TicketState.Success ?: return@withLock
                if (cur2.originalHistory is OriginalHistoryState.Loaded) return@withLock

                _ticketState.update { st ->
                    if (st is TicketState.Success) st.copy(originalHistory = OriginalHistoryState.Loading) else st
                }

                val ticketId = cur2.respond.ticket.ticketId
                runCatching { getTicketOriginalHistoryUseCase(ticketId, limit = 20) }
                    .onSuccess { items ->
                        _ticketState.update { st ->
                            if (st is TicketState.Success) st.copy(
                                originalHistory = OriginalHistoryState.Loaded(items.sortedBy { it.sentAtMs })
                            ) else st
                        }
                    }
                    .onFailure { e ->
                        _ticketState.update { st ->
                            if (st is TicketState.Success) st.copy(
                                originalHistory = OriginalHistoryState.Error(e.message ?: "Erreur chargement originaux")
                            ) else st
                        }
                    }
            }
        }
    }

    // ---------------------------------------------------------------------
    // Internals (load / preload) - unchanged logic
    // ---------------------------------------------------------------------

    /** En onglet Respond, la navigation (Suivant/Précédent) ignore les tickets fermés. */
    private fun navigableTickets(): List<Ticket> =
        tickets.filter { it.status.uppercase() != TicketStatus.CLOSED }

    private suspend fun ensureTicketsLoaded() {
        if (tickets.isNotEmpty()) return
        logV2("→ fetching /tickets limit=$batchSize assignedToMe=true (Répondre: skip/back entre mes tickets)")
        tickets = listTicketsUseCase(limit = batchSize, assignedToMe = true)
        val nav = navigableTickets()
        index = nav.firstOrNull()?.let { first -> tickets.indexOfFirst { it.ticketId == first.ticketId } } ?: 0
        preloadCache.clear()
    }

    /** Fallback when /inbox/stats fails (e.g. 502): derive open/waiting/weather from current ticket list. */
    private fun syncSidebarCountsFromTicketList() {
        _openCount.value = tickets.count { it.status == TicketStatus.OPEN }
        _waitingCount.value = tickets.count { it.status == TicketStatus.WAITING }
        val volume = _openCount.value + _waitingCount.value
        _weather.value = when {
            volume < 4 -> "fluide"
            volume < 12 -> "normal"
            volume < 25 -> "dense"
            else -> "sous_tension"
        }
        _weatherPercentDelta.value = null
    }

    private fun currentTicketOrNull(): Ticket? = tickets.getOrNull(index)

    private suspend fun loadCurrentTicket(
        forceAi: Boolean,
        transition: TicketTransitionState = TicketTransitionState.Idle,
        fromNavigation: Boolean = false
    ) {
        var t = currentTicketOrNull()
        if (t == null) {
            if (tickets.isEmpty()) {
                _ticketState.value = TicketState.Empty(canGoBack = false)
                return
            }
            index = 0
            t = currentTicketOrNull()
            if (t == null) {
                _ticketState.value = TicketState.Empty(canGoBack = false)
                return
            }
        }

        val cached = if (fromNavigation) null else preloadCache.remove(t.ticketId)
        val (respond: TicketRespondState, segment: ActiveSegment, extract: AiExtractV2?) = if (cached != null) {
            logV2("→ using preloaded data for ticketId=${t.ticketId}")
            val seg = buildActiveSegment(cached.respond)
            Triple(cached.respond, seg, cached.extract)
        } else {
            logV2("→ fetching /tickets/${t.ticketId}/respond")
            var respond: TicketRespondState = getTicketRespondStateUseCase(t.ticketId)
            var segment = buildActiveSegment(respond)

            val extract: AiExtractV2? = if (!forceAi) {
                extractOrchestrator.getCachedExtract(t.ticketId)
            } else null
                ?: segment.pivotInboundId
                    ?.takeIf { it.isNotBlank() }
                    ?.let { msgId ->
                        logV2("→ generating AI extract for messageId=$msgId force=$forceAi")
                        extractOrchestrator.ensureExtract(
                            ticketId = t.ticketId,
                            messageId = msgId,
                            force = forceAi
                        )
                    }

            if (extract != null) {
                respond = getTicketRespondStateUseCase(t.ticketId)
                segment = buildActiveSegment(respond)
            }

            Triple(respond, segment, extract)
        }

        val payload = buildLoadedTicketPayload(respond, segment, extract)
        logV2("loadCurrentTicket -> set Success(transition=$transition) ticketId=${t.ticketId}")

        _ticketState.value = TicketState.Success(
            respond = respond,
            pivotInboundMessageId = segment.pivotInboundId,
            activeInbound = segment.pivotInbound,
            activeMessages = segment.messages,
            extract = extract,
            missingKeys = payload.missingKeys,
            toConfirmKeys = payload.toConfirmKeys,
            sidebarDetected = payload.sidebarDetected,
            replyText = "",
            suggestedReplyText = payload.initialReplyText,
            stableReplyText = null,
            isRegenerating = false,
            isModified = false,
            noMoreTickets = false,
            canGoBack = index > 0,
            transition = transition,
            history = HistoryState.Hidden,
            originalHistory = OriginalHistoryState.Hidden
        )

        logV2("UI updated index=$index ticketId=${t.ticketId} activeMessages=${segment.messages.size}")
        schedulePreloadNextTickets()
        scheduleRefreshThreadOnOpen(t.ticketId)
    }

    /**
     * Refreshes the ticket's thread from Gmail in background, then reloads respond state and
     * patches the UI so the user sees up-to-date messages without blocking the open.
     */
    private fun scheduleRefreshThreadOnOpen(ticketId: String) {
        viewModelScope.launch(dispatcher) {
            runCatching { refreshTicketThreadUseCase(ticketId) }.onFailure {
                logV2("refresh-thread failed ticketId=$ticketId: ${it.message}")
                return@launch
            }
            val current = currentTicketOrNull() ?: return@launch
            if (current.ticketId != ticketId) return@launch
            val respond = getTicketRespondStateUseCase(ticketId)
            val segment = buildActiveSegment(respond)
            val payload = buildLoadedTicketPayload(respond, segment, extractOrchestrator.getCachedExtract(ticketId))
            _ticketState.update { state ->
                if (state is TicketState.Success && state.respond.ticket.ticketId == ticketId) {
                    state.copy(
                        respond = respond,
                        pivotInboundMessageId = segment.pivotInboundId,
                        activeInbound = segment.pivotInbound,
                        activeMessages = segment.messages,
                        missingKeys = payload.missingKeys,
                        toConfirmKeys = payload.toConfirmKeys,
                        sidebarDetected = payload.sidebarDetected,
                        suggestedReplyText = payload.initialReplyText
                    )
                } else state
            }
        }
    }

    private fun schedulePreloadNextTickets() {
        preloadJob?.cancel()
        val currentIndex = index
        val list = tickets
        if (list.isEmpty()) return

        preloadJob = viewModelScope.launch(dispatcher) {
            for (offset in 1..preloadAhead) {
                val i = currentIndex + offset
                val ticket = list.getOrNull(i) ?: continue
                val ticketId = ticket.ticketId
                if (preloadCache.containsKey(ticketId)) continue

                runCatching {
                    val respond = getTicketRespondStateUseCase(ticketId)
                    val segment = buildActiveSegment(respond)

                    val extract: AiExtractV2? = if (offset == 1) {
                        segment.pivotInboundId?.takeIf { it.isNotBlank() }?.let { msgId ->
                            extractOrchestrator.ensureExtract(
                                ticketId = ticketId,
                                messageId = msgId,
                                force = false
                            )
                        }
                    } else null

                    val finalRespond = if (extract != null) getTicketRespondStateUseCase(ticketId) else respond
                    preloadCache[ticketId] = PreloadedTicket(respond = finalRespond, extract = extract)

                    logV2("→ preloaded ticketId=$ticketId (offset=$offset)")
                }.onFailure { e ->
                    logV2("⚠️ preload failed ticketId=$ticketId: ${e.message}")
                }
            }
        }
    }
}
